<template>
  <div class="relative w-full min-h-screen overflow-x-clip bg-slate-50 dark:bg-[#0b1220]">
    <Sidebar
      :is-collapsed="isSidebarCollapsed"
      @toggle-sidebar="isSidebarCollapsed = !isSidebarCollapsed"
    />
    <main
      class="relative w-full min-h-screen bg-slate-50 dark:bg-[#0b1220]"
      :class="isSidebarCollapsed ? 'sm:pl-24' : 'sm:pl-56'"
    >
      <Navbar
        :title="title"
        :tabs="tabs"
        :is-collapsed="isSidebarCollapsed"
        @toggle-chatbot="showChatbot = !showChatbot"
        @toggle-sidebar="isSidebarCollapsed = !isSidebarCollapsed"
      />
      <ClientOnly>
        <AppReleaseUpdateBanner />
      </ClientOnly>
      <div class="flex items-start flex-1 w-full h-full">
        <div class="flex-1 w-full">
          <slot/>
        </div>
        <div class="fixed right-0 w-full p-2 max-w-80 xl:sticky top-16 z-30">
          <Transition name="fade">
            <ChatBot v-if="showChatbot" />
          </Transition>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
const showChatbot = ref(false);

defineProps<{
  tabs: { title: string; link: string }[];
  title: string;
}>();

// useCookie makes the initial value available during SSR so the server renders
// the correct expanded/collapsed state — eliminating the retracted→expanded flash.
// The cookie is updated automatically whenever isSidebarCollapsed changes.
const isSidebarCollapsed = useCookie<boolean>('bp-sidebar-collapsed', {
  default: () => false, // expanded by default on first visit
  sameSite: 'strict',
});

onMounted(() => {
  // On the very first visit (no cookie yet) apply a responsive heuristic:
  // collapse on narrow screens, keep expanded on desktop.
  const hasCookie = document.cookie.split(';').some(c => c.trim().startsWith('bp-sidebar-collapsed='));
  if (!hasCookie) {
    isSidebarCollapsed.value = window.innerWidth < 768;
  }
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
